import { TooltipProvider } from "@/components/ui/tooltip";
import { useReactFlow, useViewport } from "@xyflow/react";
import {
  CircleDot,
  Focus,
  Grid2x2,
  Minus,
  MousePointer2,
  Plus,
  Pointer,
  Slash,
} from "lucide-react";
import { motion } from "motion/react";
import { useCallback, useState } from "react";
import {
  CANVAS_CURSOR_TOOLS,
  CANVAS_GRID_MODES,
  type CanvasCursorTool,
  type CanvasGridMode,
} from "../canvas-controls.types";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useCanvasChromeEnterAnimation } from "../hooks/useCanvasChromeEnterAnimation";
import {
  canStepZoomIn,
  canStepZoomOut,
  stepZoomPercent,
  zoomLevelToPercent,
} from "../utils/canvasZoomUtils";
import {
  CanvasToolbarButton,
  CanvasToolbarDisplay,
  CanvasToolbarDivider,
  CanvasToolbarGroup,
  CanvasToolbarShell,
  CanvasToolbarSubmenu,
} from "./toolbar";

type OpenMenu = "cursor" | "grid" | "zoom" | null;

const GRID_ICONS: Record<CanvasGridMode, typeof Grid2x2> = {
  lines: Grid2x2,
  dots: CircleDot,
  none: Slash,
};

const CURSOR_ICONS: Record<CanvasCursorTool, typeof Pointer> = {
  arrow: MousePointer2,
  hand: Pointer,
};

export function CanvasControlsToolbar() {
  const { gridMode, setGridMode, cursorTool, setCursorTool } = useCanvasUi();
  const { zoom } = useViewport();
  const reactFlow = useReactFlow();
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);

  const zoomPercent = zoomLevelToPercent(zoom);
  const shouldAnimateChromeEnter = useCanvasChromeEnterAnimation();

  const setZoomPercent = useCallback(
    (percent: number) => {
      const { x, y } = reactFlow.getViewport();
      void reactFlow.setViewport(
        { x, y, zoom: percent / 100 },
        { duration: 200 },
      );
    },
    [reactFlow],
  );

  const handleRecenter = useCallback(() => {
    void reactFlow.fitView({ duration: 300 });
  }, [reactFlow]);

  const GridIcon = GRID_ICONS[gridMode];
  const CursorIcon = CURSOR_ICONS[cursorTool];

  return (
    <motion.div
      initial={shouldAnimateChromeEnter ? { opacity: 0, y: -12 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ opacity: { duration: 0.2 }, y: { duration: 0.2 } }}
      className="flex items-center"
      data-canvas-control
    >
      <TooltipProvider>
        <CanvasToolbarShell
          orientation="horizontal"
          aria-label="Canvas controls"
        >
          <CanvasToolbarGroup>
            <CanvasToolbarSubmenu
              open={openMenu === "cursor"}
              onOpenChange={(open) => setOpenMenu(open ? "cursor" : null)}
              side="bottom"
              trigger={
                <CanvasToolbarButton
                  label="Cursor"
                  dropdown
                  aria-expanded={openMenu === "cursor"}
                >
                  <CursorIcon className="size-4" strokeWidth={2} />
                </CanvasToolbarButton>
              }
            >
              <CanvasToolbarGroup>
                {CANVAS_CURSOR_TOOLS.map((tool) => {
                  const Icon = CURSOR_ICONS[tool.key];
                  return (
                    <CanvasToolbarButton
                      key={tool.key}
                      label={tool.name}
                      active={cursorTool === tool.key}
                      onClick={() => {
                        setCursorTool(tool.key);
                        setOpenMenu(null);
                      }}
                    >
                      <Icon className="size-4" strokeWidth={2} />
                    </CanvasToolbarButton>
                  );
                })}
              </CanvasToolbarGroup>
            </CanvasToolbarSubmenu>

            <CanvasToolbarSubmenu
              open={openMenu === "grid"}
              onOpenChange={(open) => setOpenMenu(open ? "grid" : null)}
              side="bottom"
              trigger={
                <CanvasToolbarButton
                  label="Grid"
                  dropdown
                  aria-expanded={openMenu === "grid"}
                >
                  <GridIcon className="size-4" strokeWidth={2} />
                </CanvasToolbarButton>
              }
            >
              <CanvasToolbarGroup>
                {CANVAS_GRID_MODES.map((mode) => {
                  const Icon = GRID_ICONS[mode.key];
                  return (
                    <CanvasToolbarButton
                      key={mode.key}
                      label={mode.name}
                      active={gridMode === mode.key}
                      onClick={() => {
                        setGridMode(mode.key);
                        setOpenMenu(null);
                      }}
                    >
                      <Icon className="size-4" strokeWidth={2} />
                    </CanvasToolbarButton>
                  );
                })}
              </CanvasToolbarGroup>
            </CanvasToolbarSubmenu>
          </CanvasToolbarGroup>

          <CanvasToolbarDivider />

          <CanvasToolbarGroup>
            <CanvasToolbarSubmenu
              open={openMenu === "zoom"}
              onOpenChange={(open) => setOpenMenu(open ? "zoom" : null)}
              side="bottom"
              trigger={
                <CanvasToolbarButton
                  label="Zoom"
                  dropdown
                  monoLabel={`${zoomPercent}%`}
                  aria-expanded={openMenu === "zoom"}
                />
              }
            >
              <CanvasToolbarGroup>
                <CanvasToolbarButton
                  label="Zoom out"
                  disabled={!canStepZoomOut(zoomPercent)}
                  onClick={() =>
                    setZoomPercent(stepZoomPercent(zoomPercent, -1))
                  }
                >
                  <Minus className="size-4" strokeWidth={2} />
                </CanvasToolbarButton>
                <CanvasToolbarDisplay>{zoomPercent}%</CanvasToolbarDisplay>
                <CanvasToolbarButton
                  label="Zoom in"
                  disabled={!canStepZoomIn(zoomPercent)}
                  onClick={() =>
                    setZoomPercent(stepZoomPercent(zoomPercent, 1))
                  }
                >
                  <Plus className="size-4" strokeWidth={2} />
                </CanvasToolbarButton>
              </CanvasToolbarGroup>
            </CanvasToolbarSubmenu>
          </CanvasToolbarGroup>

          <CanvasToolbarDivider />

          <CanvasToolbarGroup>
            <CanvasToolbarButton label="Recenter" onClick={handleRecenter}>
              <Focus className="size-4" strokeWidth={2} />
            </CanvasToolbarButton>
          </CanvasToolbarGroup>
        </CanvasToolbarShell>
      </TooltipProvider>
    </motion.div>
  );
}
